import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Loader2,
  Search,
  Clock,
  Smartphone,
  AlertCircle,
  CheckCircle2,
  Package,
  Wrench,
  Truck,
  Store,
  MapPin,
  ArrowLeft,
  ChevronRight,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import { getStatusColor, getStatusLabel } from "../../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_ICONS = {
  received: Package,
  diagnosed: Search,
  waiting_for_approval: Clock,
  in_progress: Wrench,
  pending_parts: Package,
  repaired: CheckCircle2,
  delivered: Truck,
  closed: CheckCircle2,
};

export default function TrackJob() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [shopInfo, setShopInfo] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState("");
  const [searched, setSearched] = useState(false);

  // Get subdomain from URL or query param
  const getSubdomain = () => {
    const querySubdomain = searchParams.get("shop");
    if (querySubdomain) return querySubdomain;

    const hostname = window.location.hostname;
    const parts = hostname.split(".");
    if (parts.length >= 3 && parts[0] !== "www") {
      return parts[0];
    }
    return null;
  };

  const subdomain = getSubdomain();

  useEffect(() => {
    if (subdomain) {
      fetchShopInfo();
    }
  }, [subdomain]);

  const fetchShopInfo = async () => {
    try {
      const res = await axios.get(`${API}/api/public/shop/${subdomain}/info`);
      setShopInfo(res.data);
    } catch (err) {
      console.error("Failed to fetch shop info:", err);
    }
  };

  const handleTrack = async (e) => {
    e.preventDefault();

    if (!phone.trim() || phone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setLoading(true);
    setError(null);
    setJobs([]);
    setSelectedJob(null);
    setSearched(true);

    try {
      const res = await axios.get(
        `${API}/api/public/shop/${subdomain}/track?phone=${encodeURIComponent(phone)}`
      );
      setJobs(res.data.jobs || []);
      // If only one job, auto-select it
      if (res.data.jobs && res.data.jobs.length === 1) {
        setSelectedJob(res.data.jobs[0]);
      }
    } catch (err) {
      console.error("Track failed:", err);
      setError(err.response?.data?.detail || "No active repairs found for this phone number.");
    } finally {
      setLoading(false);
    }
  };

  const StatusIcon = selectedJob ? STATUS_ICONS[selectedJob.status] || Package : Package;

  // Reset to search state
  const handleBack = () => {
    setSelectedJob(null);
    setJobs([]);
    setPhone("");
    setSearched(false);
    setError(null);
  };

  // Back to job list from detail view
  const handleBackToList = () => {
    setSelectedJob(null);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="track-job-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            {shopInfo?.logo_url ? (
              <img
                src={shopInfo.logo_url.startsWith("http") ? shopInfo.logo_url : `${API}${shopInfo.logo_url}`.replace("/api", "")}
                alt={shopInfo.shop_name}
                className="w-10 h-10 object-contain rounded-lg"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Store className="w-5 h-5 text-primary" />
              </div>
            )}
            <div>
              <h1 className="font-bold text-lg">{shopInfo?.shop_name || "Track Your Repair"}</h1>
              {shopInfo?.address && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {shopInfo.address}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Search Form - Only show when no job is selected */}
        {!selectedJob && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5 text-primary" />
                Track Your Repair
              </CardTitle>
              <CardDescription>
                Enter your phone number to see the status of your active repairs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTrack} className="space-y-4">
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Enter your registered phone number"
                      className="pl-10"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="track-btn">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Track Repairs
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-red-700">
                <AlertCircle className="w-5 h-5" />
                <p>{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Multiple Jobs List */}
        {searched && jobs.length > 1 && !selectedJob && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Your Active Repairs ({jobs.length})</h2>
            </div>
            <div className="space-y-3">
              {jobs.map((job) => {
                const JobStatusIcon = STATUS_ICONS[job.status] || Package;
                return (
                  <Card 
                    key={job.job_number} 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedJob(job)}
                    data-testid={`job-card-${job.job_number}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold">{job.job_number}</span>
                            <Badge className={`${getStatusColor(job.status)}`}>
                              <JobStatusIcon className="w-3 h-3 mr-1" />
                              {getStatusLabel(job.status)}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Smartphone className="w-3 h-3" />
                            <span>
                              {job.device?.type}
                              {job.device?.brand && ` - ${job.device.brand}`}
                              {job.device?.model && ` ${job.device.model}`}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                            {job.problem_description}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <Button
              variant="outline"
              onClick={handleBack}
              className="w-full"
              data-testid="back-to-search-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Search Again
            </Button>
          </div>
        )}

        {/* Single Job Detail */}
        {selectedJob && (
          <div className="space-y-4 animate-in">
            {/* Back Button (for multiple jobs) */}
            {jobs.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToList}
                className="mb-2"
                data-testid="back-to-list-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to all repairs
              </Button>
            )}

            {/* Status Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Job Number</p>
                    <p className="text-2xl font-bold">{selectedJob.job_number}</p>
                  </div>
                  <Badge className={`${getStatusColor(selectedJob.status)} text-base px-4 py-2`}>
                    <StatusIcon className="w-4 h-4 mr-2" />
                    {getStatusLabel(selectedJob.status)}
                  </Badge>
                </div>

                {/* Device Info */}
                <div className="bg-muted rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Smartphone className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">
                      {selectedJob.device?.type}
                      {selectedJob.device?.brand && ` - ${selectedJob.device.brand}`}
                      {selectedJob.device?.model && ` ${selectedJob.device.model}`}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{selectedJob.problem_description}</p>
                </div>

                {/* Diagnosis Info */}
                {selectedJob.diagnosis && (
                  <div className="bg-blue-50 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-blue-900 mb-2">Diagnosis</h4>
                    <p className="text-sm text-blue-800">{selectedJob.diagnosis}</p>
                    {selectedJob.estimated_cost && (
                      <p className="text-sm font-medium text-blue-900 mt-2">
                        Estimated Cost: ₹{selectedJob.estimated_cost}
                      </p>
                    )}
                  </div>
                )}

                <div className="text-xs text-muted-foreground">
                  <p>Created: {new Date(selectedJob.created_at).toLocaleString()}</p>
                  {selectedJob.updated_at !== selectedJob.created_at && (
                    <p>Last Updated: {new Date(selectedJob.updated_at).toLocaleString()}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            {selectedJob.timeline && selectedJob.timeline.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Repair Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    {selectedJob.timeline.map((entry, index) => {
                      const EntryIcon = STATUS_ICONS[entry.status] || Package;
                      const isLast = index === selectedJob.timeline.length - 1;
                      return (
                        <div key={index} className="flex gap-4 pb-6 last:pb-0">
                          <div className="relative flex flex-col items-center">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                isLast ? "bg-primary text-primary-foreground" : "bg-muted"
                              }`}
                            >
                              <EntryIcon className="w-4 h-4" />
                            </div>
                            {!isLast && (
                              <div className="w-0.5 h-full bg-muted absolute top-8" />
                            )}
                          </div>
                          <div className="flex-1 pt-1">
                            <p className="font-medium">{getStatusLabel(entry.status)}</p>
                            {entry.notes && (
                              <p className="text-sm text-muted-foreground">{entry.notes}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(entry.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Back Button */}
            <Button
              variant="outline"
              onClick={handleBack}
              className="w-full"
              data-testid="track-another-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Track Another Number
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
